import { cn } from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { Plural } from "@lingui/react/macro";
import { OTPInput, type SlotProps } from "input-otp";
import React from "react";
import { Show } from "~/components/show";
import { AUTH_OTP_RESEND_COOLDOWN } from "~/modules/auth/constants";
import { useAuthAction } from "~/modules/auth/data";
import { type SignInSchema, signInSchema } from "~/modules/auth/data/schema";

// You can emulate a fake textbox caret!
function FakeCaret() {
  return (
    <div className="absolute pointer-events-none inset-0 flex items-center justify-center animate-caret-blink">
      <div className="w-px h-8 bg-foreground" />
    </div>
  );
}

// Inspired by Stripe's MFA input.
function FakeDash() {
  return (
    <div className="flex w-10 justify-center items-center ">
      <div className="w-3 h-1 rounded-full bg-border bg-foreground" />
    </div>
  );
}

const Slot = (props: SlotProps) => {
  return (
    <div
      className={cn(
        "relative w-10 h-14 text-[2rem]",
        "flex items-center justify-center",
        "transition-all duration-300",
        "border-border border-y border-r first:border-l first:rounded-l-md last:rounded-r-md",
        "outline outline-0 outline-accent-foreground/40",
      )}
    >
      <div className="group-has-[input[data-input-otp-placeholder-shown]]:opacity-20">
        {props.char ?? props.char}
      </div>
      {props.isActive && <FakeCaret />}
    </div>
  );
};

export const OtpInput = ({
  email,
  redirectTo,
  ...props
}: { email: string; redirectTo?: string; "data-testid": string }) => {
  const { signIn } = useAuthAction();
  const [code, setCode] = React.useState("");
  const [isCodeValid, setIsCodeValid] = React.useState(true);
  const [resendCooldown, setResendCooldown] = React.useState(AUTH_OTP_RESEND_COOLDOWN);
  const isError = React.useMemo(
    () => !isCodeValid || signIn.isError,
    [signIn.isError, isCodeValid],
  );

  // Add cooldown timer effect
  React.useEffect(() => {
    const timer =
      resendCooldown > 0 ? setTimeout(() => setResendCooldown(resendCooldown - 1), 1000) : null;

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resendCooldown]);

  const onResend = () => {
    if (resendCooldown > 0) return;

    signIn.submit({
      intent: "sign_in:email",
      email: email,
    });
    setResendCooldown(AUTH_OTP_RESEND_COOLDOWN);
  };

  const onCodeComplete = () => {
    const validated = signInSchema.safeParse({
      intent: "sign_in:otp",
      email: email,
      code: code,
      redirectTo,
    } satisfies SignInSchema);

    if (validated.error) {
      setIsCodeValid(false);
      return;
    }

    setIsCodeValid(true);
    signIn.submit(validated.data);
  };

  return (
    <div className="w-full py-4 px-8">
      <OTPInput
        {...props}
        autoFocus
        maxLength={6}
        onChange={setCode}
        value={code}
        onComplete={onCodeComplete}
        render={({ slots }) => (
          <>
            <div className="flex justify-center">
              <div className="flex">
                {slots.slice(0, 3).map((slot, idx) => (
                  <Slot
                    key={`${idx}-${slot.char}`}
                    {...slot}
                    hasFakeCaret={idx === code.length}
                  />
                ))}
              </div>
              <FakeDash />
              <div className="flex">
                {slots.slice(3).map((slot, idx) => (
                  <Slot
                    key={`${idx}-${slot.char}`}
                    {...slot}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      />
      <Show when={isError}>
        <div className="text-danger text-center text-sm mt-2">
          {signIn.data?.error === "user_banned" ? (
            <Trans>Your account is deactivated. Please contact support.</Trans>
          ) : (
            <Trans>Invalid code. Please try again.</Trans>
          )}
        </div>
      </Show>
      <div className="flex w-full justify-center">
        <div className="mt-4 text-foreground-500 text-sm">
          <Trans>Didn't receive code?</Trans>{" "}
          <a
            className={cn(
              "font-medium cursor-pointer",
              resendCooldown > 0
                ? "text-default-400 cursor-not-allowed pointer-events-none"
                : "text-primary hover:text-primary-500",
            )}
            onClick={() => {
              setCode("");
              setIsCodeValid(true);
              onResend();
            }}
          >
            <Plural
              value={resendCooldown}
              other="Resend (#s)"
              zero="Resend"
            />
          </a>
        </div>
      </div>
    </div>
  );
};
